import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Provider, useAtomValue, useSetAtom } from 'jotai';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { InlineLoading } from '@carbon/react';

import { resourcesAtom } from 'atoms/root';
import { FullPageWizardLayout, StepConfig } from 'components/full-page-wizard';

import {
  createPackageAtom,
  createPackageDraftAtom,
  updatePackageAtom,
  updatePackageDraftAtom,
} from './atoms';
import { dismissAssetName } from './asset-list-utils';
import { PackageWizardProvider } from './context';
import { WithExportNotice } from './export-notice';
import { useAssetListSummary, useEditModeBootstrap } from './hooks';
import type {
  LargeTableConfirmation,
  PackageFormInstance,
  PackageFormValues,
  PackageWizardApi,
  PackageWizardMode,
  ScopePayload,
} from './types';
import {
  applySourceSelection,
  DraftMaterializationTimeout,
  draftPusherChannel,
  editPackageBody,
  type LastDraftWrite,
  markDraftTouched,
  sourceLockLabel,
  step0DraftWrite,
  waitForDraftMaterialization,
  wizardBreadcrumbLabel,
  wizardTitle,
} from './utils';
import { SourceTagSet, type SourceTag } from './source-tag-set';
import { SelectSourceStep } from './steps/select-source-step';
import { SelectAssetsSlotsStep } from './steps/select-assets-slots-step';
import { SummaryStep } from './steps/summary-step';

/**
 * Renders its children and reports whether they are on screen.
 *
 * The wizard mounts only the current step's content, so a step wrapped in this
 * tells the page when it is the step showing. The header needs that to decide
 * whether the source tags are removable, and cannot get it from
 * `useWizardStep()`: the title row renders above the wizard that provides it.
 *
 * @param {object} props - Mount callback and the step content to render
 * @return {JSX.Element} The children, unchanged
 */
const WhileShowing: FC<{
  onChange: (showing: boolean) => void;
  children: ReactNode;
}> = ({ onChange, children }) => {
  useEffect(() => {
    onChange(true);
    return () => onChange(false);
  }, [onChange]);

  return <>{children}</>;
};

/** A draft write that landed: where to wait for its build, and what it sent. */
interface WrittenDraft extends LastDraftWrite {
  id: string;
  channel: string;
}

/**
 * Waits for a written draft's build, noting on it whether the wait timed out
 * or the build failed.
 *
 * @param {WrittenDraft} written - The draft write to wait on
 * @return {Promise<void>} Settles as the wait does
 */
const awaitDraftBuild = async (written: WrittenDraft): Promise<void> => {
  try {
    await waitForDraftMaterialization(written.channel, written.id);
    written.unbuilt = null;
  } catch (error) {
    written.unbuilt =
      error instanceof DraftMaterializationTimeout ? 'timedOut' : 'failed';
    throw error;
  }
};

/** Props passed from the gate to the inner form once bootstrap resolves. */
export interface PackageWizardFormProps {
  mode: PackageWizardMode;
  packageId: string | null;
  defaults: PackageFormValues;
}

/**
 * Owns the TanStack Form instance and the wizard `api`. Mounts only
 * after `useEditModeBootstrap` resolves — by then `defaults.draftId`
 * is set in edit mode (Step 1 sees a non-null draft from first paint)
 * and the `mode` discriminator is locally derived from `packageId` for
 * UI labels. Steps consume `api` via `usePackageWizardCtx()` and stay
 * mode-agnostic.
 *
 * @param {PackageWizardFormProps} props - mode + packageId + hydrated defaults
 * @return {JSX.Element} The wizard layout with provider and steps
 */
export const PackageWizardForm: FC<PackageWizardFormProps> = ({
  mode,
  packageId,
  defaults,
}) => {
  const history = useHistory();
  const form = useForm({
    defaultValues: defaults,
  }) as unknown as PackageFormInstance;

  const createDraft = useSetAtom(createPackageDraftAtom);
  const updateDraft = useSetAtom(updatePackageDraftAtom);
  const createPackage = useSetAtom(createPackageAtom);
  const updatePackage = useSetAtom(updatePackageAtom);

  // The draft the last successful write landed on, the scope it sent, and how
  // the wait for its build ended. Kept so a re-Next after a materialization
  // timeout can go back to waiting on the same Pusher channel instead of
  // re-sending a scope the BE already holds, and so one after a failed build
  // of the asset list can send it again.
  const lastWrittenDraft = useRef<WrittenDraft | null>(null);

  const api = useMemo<PackageWizardApi>(
    () => ({
      upsertDraft: async (
        scope: ScopePayload,
        { confirmLargeTable }: LargeTableConfirmation = {}
      ) => {
        const { draftId } = form.state.values;
        const written = lastWrittenDraft.current;
        // POST with the full scope, PATCH with what changed (the whole asset
        // list when it or the as-of date changed, or to retry a failed build),
        // or nothing — see `step0DraftWrite`.
        const { write, dirty: dirtyForStep0 } = step0DraftWrite(
          form,
          scope,
          written?.id === draftId ? written : null
        );
        if (write.kind === 'none') {
          // Nothing to send — but the draft is not necessarily ready. A previous
          // attempt can have written this scope and then timed out waiting for
          // materialization, which leaves the write standing and the draft still
          // building. Re-await it rather than advancing to a half-built Step 1;
          // re-sending the identical scope is what the BE rejects as "nothing to
          // change". Waiting is safe when it is already ready: the watcher's
          // status check settles immediately. An edit's draft came from the
          // bootstrap rather than a write here, and a Step 1 build of it can
          // have timed out too: it is waited on all the same.
          //
          // Fields still dirty here hold the scope the BE already has (an edit
          // changed back since that write), so they are in step with it:
          // clear them as a write would, or a later unchanged Next re-sends the
          // list and discards Step 1's choices.
          for (const name of dirtyForStep0) {
            form.setFieldMeta(name, (m) => ({ ...m, isDirty: false }));
          }
          if (written?.id === draftId) await awaitDraftBuild(written);
          else if (draftId) {
            await waitForDraftMaterialization(
              draftPusherChannel(draftId),
              draftId
            );
          }
          return;
        }
        // A refusal for the table's size writes nothing, so sending the same
        // write again with the user's confirmation is the whole retry.
        const confirmation = confirmLargeTable ? { confirmLargeTable } : {};
        const draft =
          write.kind === 'update'
            ? await updateDraft({
                draftId: write.draftId,
                payload: { ...write.payload, ...confirmation },
              })
            : await createDraft({ ...write.payload, ...confirmation });

        // Commit draftId on first create. Don't `form.reset` — that would
        // re-baseline `defaultValues` for every field and corrupt Step 2's
        // diff against the package baseline. Instead, surgically clear
        // `isDirty` for the Step 0 fields we just synced so the next
        // Step 0 Next-click can short-circuit. `draftId.isTouched` (set
        // by source/date handlers) is intentionally left alone — Step 2
        // still needs to know the draft has diverged from the package.
        //
        // This runs BEFORE the materialization wait on purpose. The write has
        // already landed, so the form is in step with the BE from here on; if
        // the wait then times out, a retry must not re-send the same scope —
        // the BE answers an unchanged patch with 422 "must provide ... to
        // change", which used to leave the step stuck on Invalid for good.
        if (!draftId) form.setFieldValue('draftId', draft.id);

        // Mirror the scenario the BE stamped on the draft. Packages::Drafts::Creator never
        // leaves it null — a fresh create falls through to Legacy, the same default the
        // materializer builds the LTD population from. Without copying it here the form keeps
        // the `null` it was initialised with, and Step 1 renders "Select a scenario / Required"
        // with Next disabled, demanding a choice the BE has already made and already built
        // slots from.
        //
        // Dirty flag cleared immediately after: this is the server's value arriving, not a user
        // edit, and the edit flow seeds the same field through `defaults` where it is not dirty
        // either. `typeCurveScenarioId` is not in STEP_0_FIELDS, so this never reaches the
        // Step 0 patch diff.
        if (draft.typeCurveScenarioId !== null) {
          form.setFieldValue('typeCurveScenarioId', draft.typeCurveScenarioId);
          form.setFieldMeta('typeCurveScenarioId', (m) => ({
            ...m,
            isDirty: false,
          }));
        }

        for (const name of dirtyForStep0) {
          form.setFieldMeta(name, (m) => ({ ...m, isDirty: false }));
        }
        lastWrittenDraft.current = {
          id: draft.id,
          channel: draft.pusherChannel,
          scope,
          unbuilt: null,
        };

        await awaitDraftBuild(lastWrittenDraft.current);
        return draft;
      },
      // Step 1's scenario switch. Narrow on purpose: one field on the wire, no
      // `STEP_0_FIELDS` diffing, no dirty-flag bookkeeping. The BE routes a
      // scenario-only PATCH through `scenario_rebase`, which re-derives the LTD
      // population and leaves the asset universe alone.
      //
      // The form takes the draft's scenario as soon as the PATCH lands, before the
      // rebuild: from then on the draft IS on that scenario, even if the rebuild fails
      // (it keeps the new id over the old slots, and the BE refuses to save it until a
      // retry succeeds). `markDraftTouched` makes the edit-mode save send `draft_id`,
      // without which a scenario-only switch would never reach the package.
      setScenario: async (scenarioId: number) => {
        const { draftId } = form.state.values;
        if (!draftId) return;

        const draft = await updateDraft({
          draftId,
          payload: { typeCurveScenarioId: scenarioId },
        });
        form.setFieldValue(
          'typeCurveScenarioId',
          draft.typeCurveScenarioId ?? scenarioId
        );
        markDraftTouched(form);
        await waitForDraftMaterialization(draft.pusherChannel, draft.id);
        return draft;
      },
      savePackage: async (payload) => {
        if (packageId) {
          // Edit mode: a field-by-field diff body. Empty body → no-op redirect.
          const wireBody = editPackageBody(form);
          if (Object.keys(wireBody).length === 0) {
            return { href: `/packages/${packageId}` };
          }
          const updated = await updatePackage({ packageId, payload: wireBody });
          return { href: updated.href };
        }
        // Create mode requires a non-null draft_id — the form always has
        // one by this point (Step 0 sync sets it before Step 2 mounts).
        if (!payload.draft_id) {
          throw new Error('Cannot create package without a draft id');
        }
        const created = await createPackage({
          ...payload,
          draft_id: payload.draft_id,
        });
        return { href: created.href };
      },
    }),
    [form, packageId, createDraft, updateDraft, createPackage, updatePackage]
  );

  // The title counts the Step 0 selection and the tags beside it name each one,
  // so the header answers "what am I building this from" at every step — Step 1
  // and Step 2 don't show the source tables. Removing a tag goes through the
  // same rule the tables use, and is offered only where Next would rebuild the
  // draft behind it — see `sourceLockLabel`.
  const sources = useStore(form.store, (s) => s.values.sources);
  const sourceType = useStore(form.store, (s) => s.values.sourceType);
  const removeSource = useCallback(
    (id: number) => {
      if (sourceType === null) return;
      applySourceSelection(form, sourceType, { added: [], removed: [id] });
    },
    [form, sourceType]
  );

  // Seeded rather than defaulted to false: create mode opens on Step 0, so
  // starting at false would render the tags locked for the first frame.
  const [onSourceStep, setOnSourceStep] = useState(mode === 'create');

  // On the asset list the title counts the matched names and the tags name
  // them, keyed by lowercase name; removing one takes it out of the list. A
  // Subtract's tag shows the Subtract: as its Tract's preview row spells it when
  // it nests there, else as listed.
  const assetList = useAssetListSummary(form);
  const assetListTags = useMemo(
    () =>
      assetList.matched.map(({ key, name, tract, subtractSalesforceId }) => ({
        id: key,
        name: subtractSalesforceId
          ? (tract?.subtracts.find(
              (subtract) => subtract.salesforceId === subtractSalesforceId
            )?.name ?? name)
          : (tract?.name ?? name),
      })),
    [assetList.matched]
  );
  const dismissAssetListName = useCallback(
    (key: string) =>
      form.setFieldValue('assetList', (prev) => dismissAssetName(prev, key)),
    [form]
  );
  // Beside Map Query or Existing Package, the list's names follow the source's
  // own tags (numeric ids; a name's key is a string), and the title counts them
  // all.
  const tags = useMemo<SourceTag<number | string>[]>(() => {
    if (sourceType === 'assetList') return assetListTags;
    if (sourceType === 'mapQuery' || sourceType === 'package') {
      return [...sources, ...assetListTags];
    }
    return sources;
  }, [sourceType, sources, assetListTags]);
  const removeTag = useCallback(
    (id: number | string) =>
      typeof id === 'number' ? removeSource(id) : dismissAssetListName(id),
    [removeSource, dismissAssetListName]
  );
  const lockLabel = sourceLockLabel(mode, onSourceStep, tags.length);

  // Each step with the Step 1 export's message above it, so the message shows
  // on whichever step is open.
  const steps: StepConfig[] = [
    {
      label: 'Select Source',
      content: (
        <WhileShowing onChange={setOnSourceStep}>
          <SelectSourceStep creating={mode === 'create'} />
        </WhileShowing>
      ),
    },
    {
      label: 'Select Assets & Properties',
      content: <SelectAssetsSlotsStep />,
    },
    {
      label: 'Summary',
      content: <SummaryStep />,
      nextLabel: mode === 'edit' ? 'Save' : 'Create Package',
      ...(mode === 'edit' && { isInitialStep: true }),
    },
  ].map((step) => ({
    ...step,
    content: <WithExportNotice>{step.content}</WithExportNotice>,
  }));

  return (
    <PackageWizardProvider form={form} api={api}>
      <FullPageWizardLayout
        breadcrumbs={[['/packages', 'Packages']]}
        title={wizardTitle(mode, tags.length)}
        breadcrumbLabel={wizardBreadcrumbLabel(mode)}
        titleAdornment={
          <SourceTagSet
            sources={tags}
            onRemove={removeTag}
            lockLabel={lockLabel}
          />
        }
        steps={steps}
        onCancel={() => history.push('/packages')}
      />
    </PackageWizardProvider>
  );
};

/**
 * Loading-gate wrapper. Subscribes to `resourcesAtom` so search atoms
 * can resolve their endpoint paths before any step mounts, then defers
 * to `useEditModeBootstrap`. Both `'unsupported'` (legacy package) and
 * `'error'` (any failure in the GET → POST → Pusher chain) redirect to
 * `/packages` rather than render an in-page recovery UI — the wizard
 * never mounts unless bootstrap reaches `'ready'`.
 *
 * In create mode (`packageId === null`) the bootstrap resolves
 * synchronously to `createFormDefaults()` (on Map Query while it is
 * offered), so the user sees no additional load time vs. before this
 * refactor.
 *
 * @return {JSX.Element | null} Gate content
 */
const PackageWizardContent: FC = () => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const resources = useAtomValue(resourcesAtom);
  const packageId = id ?? null;
  const bootstrap = useEditModeBootstrap(packageId);

  const goToPackagesList = useCallback(
    () => history.replace('/packages'),
    [history]
  );

  // Redirect runs as an effect (not in render) so React-Router history
  // mutations don't fire during render commit.
  useEffect(() => {
    if (bootstrap.state === 'unsupported' || bootstrap.state === 'error') {
      goToPackagesList();
    }
  }, [bootstrap.state, goToPackagesList]);

  if (resources === undefined) return null;

  if (bootstrap.state !== 'ready') {
    return <InlineLoading description="Loading package..." />;
  }

  return (
    <PackageWizardForm
      mode={packageId === null ? 'create' : 'edit'}
      packageId={packageId}
      defaults={bootstrap.defaults}
    />
  );
};

/**
 * Route component for the create / edit Package wizard. Wraps the wizard
 * content in a scoped Jotai `<Provider>` so atoms reset on route re-entry
 * and all downstream `useAtom*` reads resolve against the wizard-scoped
 * store (not the app-wide default store).
 *
 * @return {JSX.Element} Scoped wizard page
 */
export const PackageWizardPage: FC = () => (
  <Provider>
    <PackageWizardContent />
  </Provider>
);
